import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CopyFormStage } from './SessionsCopyDialog.types';
import { TextFieldProps, Typography } from '@material-ui/core';
import { CancelButton, NextButton, useStyles } from './SessionsCopyDialog.styles';
import { CopyFormHeader } from './CopyFormHeader.component';
import { BasicDataForm } from './BasicDataForm.component';
import useDebounce from '../../../hooks/useDebounce';
import { verifyDuplicateExamSessionName } from '../../../services/validation/validation.service';
import { useLayoutContext } from '../../../Context/Layout.context';
import {
  getLatestCopyExamSheetOperation,
  getSessionsFromSystemOne,
  startCopyingProcess,
} from '../../../services/exams/sessions/sessions.service';
import { SessionCopyBackendCode } from '../../../components/ExamResultsRepository/ExamResultsRepository.types';
import { CopyExamSheetDto, CopyExamSheetStatus, ExamSessionSimpleDto } from '../../../models/examSessionDto.types';
import { SheetDefinitionForm } from './SheetDefinitionForm.component';
import { Prompt } from '../../../components/Prompt/Prompt.component';

interface SessionsCopyDialogContentProps {
  sheetId: string;
  onClose: () => void;
}

const maxYear = 9999;
const minMonth = 1;
const maxMonth = 12;

export const SessionsCopyDialogContent: React.FC<SessionsCopyDialogContentProps> = ({
  sheetId,
  onClose,
}: SessionsCopyDialogContentProps) => {
  const { openAlert } = useLayoutContext();
  const classes = useStyles();

  const [currentStage, setCurrentStage] = useState<CopyFormStage>(CopyFormStage.BasicData);
  const [useExistingSession, setUseExistingSession] = useState<boolean>(false);
  const textInputRef = useRef<TextFieldProps>();
  const [name, setName] = useState<string>('');
  const [validName, setValidName] = useState<boolean>(true);
  const [isTrainingSession, setIsTrainingSession] = useState<boolean>(false);
  const [selectedSession, setSelectedSession] = useState<ExamSessionSimpleDto | null>(null);
  const [availableSessions, setAvailableSessions] = useState<ExamSessionSimpleDto[]>([]);
  const [examSheetName, setExamSheetName] = useState<string>('');
  const [errorElement, setErrorElement] = useState<JSX.Element | null>(null);
  const [code, setCode] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const submissionPending = useRef(false);
  const submissionCancellation = useRef<AbortController>();
  const submission = useRef<{ fingerprint: string; requestId: string; operationId?: string }>();
  const knownOperation = useRef<{ sheetId: string; status: CopyExamSheetStatus }>();
  const [operationRead, setOperationRead] = useState<{
    sheetId: string;
    status: CopyExamSheetStatus | null;
    readError: boolean;
  }>({ sheetId: '', status: null, readError: false });
  const debouncedInputName = useDebounce(name, 500);

  const [type, setType] = useState<string>('');
  const [year, setYear] = useState<number>(2000);
  const [month, setMonth] = useState<number>(1);

  useEffect(() => {
    const cancellation = new AbortController();

    submissionCancellation.current = cancellation;
    submissionPending.current = false;
    setSubmitting(false);

    return () => cancellation.abort();
  }, [sheetId]);

  useEffect(() => {
    if (submitting) return;

    const cancellation = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const read = async () => {
      let readError = false;

      try {
        const result = await getLatestCopyExamSheetOperation(sheetId, cancellation.signal);

        if (cancellation.signal.aborted || submissionPending.current) return;

        if (result !== null) knownOperation.current = { sheetId, status: result.status };

        if (result?.status === 'Failed' && submission.current?.operationId === result.operationId) {
          submission.current = undefined;
        }

        readError = result === null && knownOperation.current?.sheetId === sheetId;
      } catch {
        if (cancellation.signal.aborted) return;

        readError = true;
      }

      const status = knownOperation.current?.sheetId === sheetId ? knownOperation.current.status : null;

      setOperationRead({ sheetId, status, readError });

      if (readError || (status !== null && status !== 'Succeeded' && status !== 'Failed')) {
        timer = setTimeout(read, 10000);
      }
    };

    read();

    return () => {
      cancellation.abort();

      if (timer !== undefined) clearTimeout(timer);
    };
  }, [sheetId, submitting]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    switch (event.target.id) {
      case 'type':
        setType(event.target.value.trimStart());
        break;
      case 'year':
        if (event.target.value.trimStart().length) {
          const providedYear = Number(event.target.value.trimStart());

          if (providedYear > maxYear || providedYear < 0) setYear(new Date().getFullYear());
          else setYear(providedYear);
        }
        break;
      case 'month':
        const providedMonth = Number(event.target.value.trimStart());

        if (providedMonth > maxMonth || providedMonth < minMonth) setMonth(1);
        else setMonth(providedMonth);
        break;
      case 'name':
        setName(event.target.value.trim());
        break;
      case 'examSheetName':
        setExamSheetName(event.target.value.trimStart());
        break;
      case 'code':
        setCode(event.target.value.trimStart());
        break;
    }
  };

  const loadSessions = useCallback(
    (signal: AbortSignal) => {
      if (useExistingSession) {
        getSessionsFromSystemOne(signal)
          .then((res) => {
            setAvailableSessions(res);
          })
          .catch(() => {
            openAlert('get_error');
          });
      }
    },
    [useExistingSession],
  );

  useEffect(() => {
    const abort = new AbortController();

    loadSessions(abort.signal);

    return () => {
      abort.abort();
    };
  }, [useExistingSession]);

  useEffect(() => {
    if (debouncedInputName) {
      verifyDuplicateExamSessionName(debouncedInputName).then(
        (result) => {
          setValidName(result);
        },
        () => openAlert('error'),
      );
    } else {
      setValidName(true);
    }
  }, [debouncedInputName]);

  const checkNextDisabled = () => {
    if (useExistingSession && selectedSession !== null) return false;

    if (name === '' || !validName) return true;
    if (type === '') return true;

    return false;
  };

  const handleStartCopyProcess = async () => {
    const cancellation = submissionCancellation.current;

    if (submissionPending.current || !cancellation || cancellation.signal.aborted) return;

    const copyInfo: Omit<CopyExamSheetDto, 'requestId'> = {
      examCode: code,
      examName: examSheetName,
      examSessionName: name,
      examSessionId: selectedSession ? selectedSession?.id : null,
      examSessionType: type,
      isTraining: selectedSession ? null : isTrainingSession,
      month: selectedSession ? null : month,
      year: selectedSession ? null : year,
    };
    const fingerprint = JSON.stringify({ sheetId, ...copyInfo });
    const reconcileFailed =
      submission.current?.fingerprint === fingerprint &&
      operationRead.sheetId === sheetId &&
      operationRead.status === 'Failed' &&
      !operationRead.readError;

    if (submission.current?.fingerprint !== fingerprint) {
      submission.current = {
        fingerprint,
        requestId: (window.crypto as Crypto & { randomUUID: () => string }).randomUUID(),
      };
    }

    submissionPending.current = true;
    setSubmitting(true);

    try {
      let response = await startCopyingProcess(
        sheetId,
        { ...copyInfo, requestId: submission.current.requestId },
        cancellation.signal,
      );

      if (cancellation.signal.aborted) return;

      if (
        reconcileFailed &&
        response.copyExamSheetResponse === SessionCopyBackendCode.Success &&
        response.operationId &&
        response.status === 'Failed'
      ) {
        submission.current = {
          fingerprint,
          requestId: (window.crypto as Crypto & { randomUUID: () => string }).randomUUID(),
        };

        response = await startCopyingProcess(
          sheetId,
          { ...copyInfo, requestId: submission.current.requestId },
          cancellation.signal,
        );

        if (cancellation.signal.aborted) return;
      }

      if (response.copyExamSheetResponse === SessionCopyBackendCode.Success) {
        if (!response.operationId || !response.status) {
          openAlert('save_error');

          return;
        }

        knownOperation.current = { sheetId, status: response.status };
        setOperationRead({ sheetId, status: response.status, readError: false });
        submission.current =
          response.status === 'Failed' ? undefined : { ...submission.current, operationId: response.operationId };

        if (response.status === 'Failed') {
          openAlert('error', 'Nie udało się skopiować arkusza.');
        } else if (response.status === 'Stopping' || response.status === 'Cleaning') {
          openAlert('error', 'Trwa sprzątanie nieudanej kopii arkusza.');
        } else {
          openAlert(
            'success',
            response.status === 'Succeeded' ? 'Ukończono kopiowanie arkusza.' : 'Zlecono kopiowanie arkusza.',
          );
          onClose();
        }
      } else if (response.copyExamSheetResponse === SessionCopyBackendCode.AnotherCopyInProgress) {
        setErrorElement(
          <Typography variant="body1" className={classes.validationErrorText}>
            {'Nie możesz rozpocząć kopiowania arkusza, ponieważ aktualnie trwa kopiowanie innego arkusza: '}
            <span className={classes.validationErrorTextBold}>{response.sheetCode}</span>
          </Typography>,
        );
      } else {
        openAlert('save_error');
      }
    } catch {
      if (!cancellation.signal.aborted) openAlert('save_error');
    } finally {
      if (!cancellation.signal.aborted) {
        submissionPending.current = false;
        setSubmitting(false);
      }
    }
  };

  const handleSelectedSession = (id: string | null) => {
    if (!id || id === '') {
      setSelectedSession(null);

      return;
    }

    const found = availableSessions.find((x) => x.id === id);

    if (found) {
      setSelectedSession(found);
    }
  };

  const isNextEnabled = checkNextDisabled();
  const isSaveEnabled = currentStage === CopyFormStage.SheetDefinition && (code === '' || examSheetName === '');

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'inline-flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ width: '100%', height: 'calc(100% - 48px)', overflowY: 'auto' }}>
        <CopyFormHeader currentStage={currentStage} />
        {currentStage === CopyFormStage.BasicData && (
          <BasicDataForm
            textInputRef={textInputRef}
            name={name}
            validName={validName}
            isTrainingSession={isTrainingSession}
            handleIsTrainingSession={(val) => setIsTrainingSession(val)}
            type={type}
            year={year}
            month={month}
            handleChange={handleChange}
            useExistingSession={useExistingSession}
            handleUseExistingSessionChange={() => setUseExistingSession((prev) => !prev)}
            availableSessions={availableSessions}
            selectedSession={selectedSession}
            setSelectedSession={handleSelectedSession}
          />
        )}
        {currentStage === CopyFormStage.SheetDefinition && (
          <SheetDefinitionForm examSheetName={examSheetName} code={code} handleChange={handleChange} />
        )}
      </div>
      <div style={{ padding: '0px 12px' }}>
        <div style={{ width: '100%', display: 'inline-flex', justifyContent: 'flex-end', gap: 8 }}>
          <CancelButton onClick={() => onClose()} disabled={submitting}>
            Anuluj
          </CancelButton>
          {currentStage === CopyFormStage.BasicData && (
            <NextButton onClick={() => setCurrentStage(CopyFormStage.SheetDefinition)} disabled={isNextEnabled}>
              Dalej
            </NextButton>
          )}
          {currentStage === CopyFormStage.SheetDefinition && (
            <NextButton onClick={() => handleStartCopyProcess()} disabled={isSaveEnabled || submitting}>
              Zapisz
            </NextButton>
          )}
        </div>
      </div>
      <Prompt
        type="error"
        open={errorElement !== null}
        enhancedText={errorElement ? errorElement : <></>}
        mainActionButtonText="OK"
        onConfirm={() => {
          setErrorElement(null);
        }}
      />
    </div>
  );
};
